import { GlobalWorkerOptions, type PDFPageProxy, getDocument } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { Icon } from '@column/column-ui-kit';
import { Skeleton, Spinner } from '@column/ui';

import { DownloadIcon } from '~/elements';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface CheckPreviewQuickLookProps {
  hasRenderError: boolean;
  isLoading: boolean;
  isOpen: boolean;
  isRendering: boolean;
  onOpenChange: (open: boolean) => void;
  pages: PDFPageProxy[];
  pdfFileName: string;
  pdfFileUrl?: string;
}

interface PDFPageCardProps {
  page: PDFPageProxy;
}

interface CheckPreviewThumbnailsProps {
  hasRenderError: boolean;
  isLoading: boolean;
  isRendering: boolean;
  onOpen: () => void;
  pages: PDFPageProxy[];
}

const ThumbnailGrid = styled.div`
  display: flex;
  max-width: 420px;
  margin-left: 8px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-left: 28px;
  }
`;

const ThumbnailButton = styled.button`
  appearance: none;
  display: block;
  width: 100%;
  aspect-ratio: 6 / 2.4;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: ${({ theme }) => theme.body};
  border: 0;
  border-radius: 8px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(200)};
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow:
      0 0 0 1px ${({ theme }) => theme.primary.background},
      0 1px 3px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary.background};
    outline-offset: 2px;
  }
`;

const ThumbnailHoverZoom = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  background: rgba(0 0 0 / 40%);
  border-radius: 9999px;
  opacity: 0;
  backdrop-filter: blur(4px);
  transform: translate(-50%, -50%) scale(0.9);
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;

  svg {
    --icon-size: 16px;
    --icon-color: #ffffff;
  }

  ${ThumbnailButton}:hover & {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const ThumbnailCanvas = styled.canvas`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
`;

const ThumbnailSkeleton = styled(Skeleton)`
  width: 100%;
  aspect-ratio: 6 / 2.4;
  border-radius: 8px;
`;

const ThumbnailStatus = styled.div`
  display: flex;
  align-items: center;
  min-height: 96px;
  margin-left: 8px;
  color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  font-size: 14px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-left: 28px;
  }
`;

const QuickLookBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  justify-items: center;
  gap: 24px;
  padding: 48px 24px 24px;
  overflow: hidden;
  background: rgba(0 0 0 / 40%);
`;

const PDFViewport = styled.div`
  position: relative;
  z-index: 11;
  width: 100%;
  height: 100%;
  max-width: min(960px, 90vw);
`;

const PDFPagesContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  gap: 16px;
  overflow-y: scroll;
  border-radius: 8px;
  overscroll-behavior: contain;
  scrollbar-color: rgba(0 0 0 / 40%) rgba(0 0 0 / 8%);
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    appearance: none;
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0 0 0 / 8%);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0 0 0 / 40%);
    border: 3px solid transparent;
    border-radius: 9999px;
    background-clip: padding-box;
  }
`;

const PDFPageContainer = styled.div`
  width: 100%;
  flex: none;
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
`;

const ViewerStatus = styled(PDFPageContainer)`
  display: flex;
  min-height: 240px;
  align-items: center;
  justify-content: center;
  color: #737373;
  font-size: 14px;
`;

const DownloadPDFButton = styled.a`
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px 8px 12px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  text-decoration: none;
  background-color: rgba(0 0 0 / 60%);
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(0 0 0 / 20%) inset;
  backdrop-filter: blur(4px);
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0 0 0 / 80%);
  }

  svg {
    --icon-size: 16px;
    --icon-color: #fff;
  }
`;

const PDFPageThumbnail: React.FC<{ onOpen: () => void; page: PDFPageProxy }> = ({ onOpen, page }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const unscaledViewport = page.getViewport({ scale: 1 });
    const renderScale = (420 / unscaledViewport.width) * window.devicePixelRatio;
    const viewport = page.getViewport({ scale: renderScale });
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const renderTask = page.render({ canvas, canvasContext: context, viewport });
    void renderTask.promise.catch(() => undefined);

    return () => renderTask.cancel();
  }, [page]);

  return (
    <ThumbnailButton
      type="button"
      aria-label={`Open check preview page ${page.pageNumber}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      <ThumbnailHoverZoom>
        <Icon.Search />
      </ThumbnailHoverZoom>
      <ThumbnailCanvas ref={canvasRef} />
    </ThumbnailButton>
  );
};

export const CheckPreviewThumbnails: React.FC<CheckPreviewThumbnailsProps> = ({
  hasRenderError,
  isLoading,
  isRendering,
  onOpen,
  pages,
}) => {
  if (isLoading || isRendering) {
    return (
      <ThumbnailGrid>
        <ThumbnailSkeleton aria-label="Loading check preview" />
      </ThumbnailGrid>
    );
  }

  if (hasRenderError) {
    return <ThumbnailStatus>The rendered check preview could not be displayed.</ThumbnailStatus>;
  }

  return <ThumbnailGrid>{pages[0] && <PDFPageThumbnail onOpen={onOpen} page={pages[0]} />}</ThumbnailGrid>;
};

export const useCheckPreviewPages = (pdfFileUrl?: string) => {
  const [pages, setPages] = useState<PDFPageProxy[]>([]);
  const [hasRenderError, setRenderError] = useState(false);
  const [isRendering, setRendering] = useState(false);

  useEffect(() => {
    if (!pdfFileUrl) {
      setPages([]);
      setRenderError(false);
      setRendering(false);
      return;
    }

    let isCancelled = false;
    setRenderError(false);
    setRendering(true);
    const loadingTask = getDocument(pdfFileUrl);
    void loadingTask.promise
      .then(async (document) =>
        Promise.all(Array.from({ length: document.numPages }, (_, index) => document.getPage(index + 1)))
      )
      .then((loadedPages) => {
        if (!isCancelled) {
          setPages(loadedPages);
          setRendering(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setRenderError(true);
          setRendering(false);
        }
      });

    return () => {
      isCancelled = true;
      void loadingTask.destroy();
    };
  }, [pdfFileUrl]);

  return { hasRenderError, isRendering, pages };
};

const PDFPageCard: React.FC<PDFPageCardProps> = ({ page }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const viewport = page.getViewport({ scale: 2 });
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const renderTask = page.render({ canvas, canvasContext: context, viewport });
    void renderTask.promise.catch(() => undefined);

    return () => renderTask.cancel();
  }, [page]);

  return (
    <PDFPageContainer>
      <canvas ref={canvasRef} className="block h-auto w-full" />
    </PDFPageContainer>
  );
};

export const CheckPreviewQuickLook: React.FC<CheckPreviewQuickLookProps> = ({
  hasRenderError,
  isLoading,
  isOpen,
  isRendering,
  onOpenChange,
  pages,
  pdfFileName,
  pdfFileUrl,
}) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onOpenChange]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <QuickLookBackdrop role="dialog" aria-label="Check Preview" aria-modal="true" onClick={() => onOpenChange(false)}>
      <PDFViewport>
        <PDFPagesContainer onClick={(event) => event.stopPropagation()}>
          {(isLoading || isRendering) && (
            <ViewerStatus>
              <Spinner className="size-5" />
            </ViewerStatus>
          )}
          {!isLoading && !isRendering && pages.map((page) => <PDFPageCard key={page.pageNumber} page={page} />)}
          {!isLoading && !isRendering && hasRenderError && (
            <ViewerStatus>
              The rendered check preview could not be displayed. You can still download the PDF.
            </ViewerStatus>
          )}
        </PDFPagesContainer>
      </PDFViewport>
      {pdfFileUrl && (
        <DownloadPDFButton href={pdfFileUrl} download={pdfFileName} onClick={(event) => event.stopPropagation()}>
          <DownloadIcon />
          Download
        </DownloadPDFButton>
      )}
    </QuickLookBackdrop>,
    document.body
  );
};
